/**
 * offer-pill.js  —  Eurus theme
 * ─────────────────────────────────────────────────────────
 * Supports multiple offer pills on the same page
 * (PDP + multiple quick-view cards simultaneously).
 *
 * Each pill has:
 *   id="offer-pill-{productId}-{sectionId}"
 *   data-variant-input-id="update-variant-{sectionId}"   ← Eurus input id
 *   data-variant-offers='{...json...}'
 *   data-variant-codes='{...json...}'                    ← coupon codes from metafield
 *
 * Metafield: custom.offer_coupon_code (per variant)
 *   Single offer  →  "SAVE10"
 *   Multi-offer   →  "SAVE10,SAVE20"   (comma matches comma-separated labels)
 *
 * Comma  ,  → two side-by-side buttons
 *   label: "Buy 2 @ ₹599, Buy 3 @ ₹899"
 *   code:  "SAVE10,SAVE20"
 *
 * Pipe   |  → stacked lines inside one pill / button
 *   label: "🔥 Best Value|Buy 2 for ₹1299|Save ₹99 instantly"
 *   code:  "SAVE10"
 *
 * Both combined:
 *   label: "🔥 Buy 2|₹599, 🔥 Buy 3|₹899"
 *   code:  "SAVE10,SAVE20"
 * ─────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /* ─── Helpers ─────────────────────────────────────────── */

  function decodeEntities(str) {
    var ta = document.createElement('textarea');
    ta.innerHTML = str;
    return ta.value;
  }

  function getOffers(pill) {
    try {
      return JSON.parse(decodeEntities(pill.getAttribute('data-variant-offers') || '{}'));
    } catch (e) {
      return {};
    }
  }

  function getCodes(pill) {
    try {
      return JSON.parse(decodeEntities(pill.getAttribute('data-variant-codes') || '{}'));
    } catch (e) {
      return {};
    }
  }

  /**
   * Split a raw label string on commas → array of segments.
   * Each segment may itself contain pipes (handled in linesToHTML).
   *   "Buy 2 @ ₹599, Buy 3 @ ₹899" → ["Buy 2 @ ₹599", "Buy 3 @ ₹899"]
   */
  function parseSegments(raw) {
    if (!raw || !raw.trim()) return [];
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /**
   * Convert a single segment into stacked <span> lines (pipe-split).
   *   "🔥 Best Value|Buy 2 for ₹1299" →
   *   <span class="offer-pill__line">🔥 Best Value</span>
   *   <span class="offer-pill__line">Buy 2 for ₹1299</span>
   */
  function linesToHTML(segment) {
    return segment
      .split('|')
      .map(function (l) { return l.trim(); })
      .filter(Boolean)
      .map(function (l) {
        return '<span class="offer-pill__line">' + l + '</span>';
      })
      .join('');
  }

  /**
   * Build the full pill innerHTML from an array of label segments
   * and a raw coupon-code string (comma-separated, matching segments).
   *
   * codesRaw examples:
   *   "SAVE10"         → single offer code
   *   "SAVE10,SAVE20"  → two codes, one per button
   *   ""               → falls back to "WELCOME"
   */
  function buildHTML(segments, codesRaw) {
    if (!segments.length) return '';

    // Split codes to match each label segment
    var codes = (codesRaw || '')
      .split(',')
      .map(function (c) { return c.trim(); });

    function buildBlock(seg, i) {
      // Get the code for this button index; fall back to WELCOME if blank
      var code = (codes[i] && codes[i] !== '') ? codes[i] : 'WELCOME';

      return (
        '<button type="button" class="offer-pill__btn" data-offer-index="' + i + '">' +
          linesToHTML(seg) +
          '<div class="offer-pill__footer">' +
            '<span class="offer-pill__code">Code: <span>' + code + '</span></span>' +
            '<span class="offer-pill__copy" data-code="' + code + '"></span>' +
            '<span class="offer-pill__copied">Copied!</span>' +
          '</div>' +
        '</button>'
      );
    }

    return segments.map(buildBlock).join('');
  }

  /* ─── Per-pill update ──────────────────────────────────── */

  function updatePill(pill, variantId) {
    if (!pill || !variantId) return;
    var id = String(variantId).trim();

    // Skip if same variant already showing
    if (pill._lastId === id) return;
    pill._lastId = id;

    // Offer label for this variant
    var raw = (getOffers(pill)[id] || '').trim();

    // Coupon code(s) for this variant (from metafield, comma-separated for multi)
    var allCodes = getCodes(pill);
    var codesRaw = (allCodes[id] || '').trim();

    var segments = parseSegments(raw);
    var html     = buildHTML(segments, codesRaw);

    pill.innerHTML = html;
    pill.classList.toggle('offer-pill--multi', segments.length > 1);

    if (html) {
      var plainLabels = segments.map(function (s) {
        return s.split('|').map(function (l) { return l.trim(); }).join(' ');
      });
      pill.setAttribute('aria-label',
        (segments.length === 1 ? 'Special offer: ' : 'Special offers: ') +
        plainLabels.join(', '));
    } else {
      pill.removeAttribute('aria-label');
    }
  }

  /* ─── Wire up a single pill to its variant input ──────── */

  function wirePill(pill) {
    if (pill._wired) return;
    pill._wired = true;

    var inputId = pill.getAttribute('data-variant-input-id');
    var input   = inputId ? document.getElementById(inputId) : null;

    // Fallback: closest form's input[name="id"]
    if (!input) {
      var form = pill.closest('form') ||
                 (pill.closest('[x-data]') &&
                  pill.closest('[x-data]').querySelector('form[action*="/cart/add"]'));
      if (form) input = form.querySelector('input[name="id"]');
    }

    // Last resort: any update-variant input
    if (!input) {
      input = document.querySelector('[id^="update-variant-"]');
    }

    if (!input) return;

    // 1. Initial render
    if (input.value) updatePill(pill, input.value);

    // 2. Intercept .value setter (Eurus updates input.value via Alpine JS)
    if (!input._pillPatched) {
      input._pillPatched = true;
      var desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (desc && desc.set) {
        var nativeSetter = desc.set;
        Object.defineProperty(input, 'value', {
          configurable: true,
          enumerable: true,
          get: desc.get,
          set: function (v) {
            nativeSetter.call(this, v);
            document
              .querySelectorAll('[data-variant-input-id="' + inputId + '"]')
              .forEach(function (p) { updatePill(p, v); });
          }
        });
      }
    }

    // 3. .input-radio change (Eurus primary swatch trigger)
    var container = input.closest('[id^="choose-options-content"]') ||
                    input.closest('[x-data]') ||
                    document.body;

    container.addEventListener('change', function (e) {
      if (e.target.classList.contains('input-radio')) {
        setTimeout(function () {
          if (input.value) updatePill(pill, input.value);
        }, 60);
      }
    });

    // 4. Polling safety-net (300 ms) — catches Alpine silent updates
    var last = input.value;
    setInterval(function () {
      var cur = input.value;
      if (cur && cur !== last) {
        last = cur;
        updatePill(pill, cur);
      }
    }, 300);
  }

  /* ─── Init: wire all pills currently in the DOM ────────── */

  function initAllPills() {
    document.querySelectorAll('.offer-pill--pdp').forEach(function (pill) {
      wirePill(pill);
    });
  }

  /* ─── Watch for pills added dynamically (quick-view load) ─ */

  function observeNewPills() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('offer-pill--pdp')) {
            wirePill(node);
          }
          node.querySelectorAll &&
            node.querySelectorAll('.offer-pill--pdp').forEach(function (p) {
              wirePill(p);
            });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ─── popstate (browser back / forward) ───────────────── */

  window.addEventListener('popstate', function () {
    var v = new URLSearchParams(window.location.search).get('variant');
    if (!v) return;
    document.querySelectorAll('.offer-pill--pdp').forEach(function (pill) {
      pill._lastId = null; // force re-render
      updatePill(pill, v);
    });
  });

  /* ─── Boot ─────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllPills();
      observeNewPills();
    });
  } else {
    initAllPills();
    observeNewPills();
  }

})();


/* ─── Copy-to-clipboard handler ───────────────────────────── */

document.addEventListener('click', function (e) {
  var btn = e.target.closest('.offer-pill__copy');
  if (!btn) return;

  var code = btn.dataset.code;

  navigator.clipboard.writeText(code).then(function () {
    var msg = btn.parentElement.querySelector('.offer-pill__copied');
    msg.style.display = 'inline';
    setTimeout(function () {
      msg.style.display = 'none';
    }, 1200);
  });
});


/* ─── Slider JS ────────────────────────────────────────────── */

(function () {

  function initSlider(slider) {
    var track = slider.querySelector('.offer-pill-track');

    function checkOverflow() {
      if (!track) return;
      var hasOverflow = track.scrollWidth > track.clientWidth + 5;
      slider.classList.toggle('active', hasOverflow);
    }

    function runChecks() {
      checkOverflow();
      setTimeout(checkOverflow, 150);
      setTimeout(checkOverflow, 400);
      setTimeout(checkOverflow, 800);
    }

    runChecks();

    window.addEventListener('resize', checkOverflow);

    var observer = new MutationObserver(function () {
      runChecks();
    });

    observer.observe(track, {
      childList: true,
      subtree: true
    });

    slider.addEventListener('click', function (e) {
      var left  = e.target.closest('.offer-pill-arrow.left');
      var right = e.target.closest('.offer-pill-arrow.right');

      if (!left && !right) return;

      var scrollAmount = track.clientWidth;

      if (left)  track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      if (right) track.scrollBy({ left:  scrollAmount, behavior: 'smooth' });
    });
  }

  function initAll() {
    document.querySelectorAll('.offer-pill-slider').forEach(initSlider);
  }

  document.addEventListener('DOMContentLoaded', initAll);

})();
